import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "@shared/hooks/use-toast";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import { ArrowLeft } from "lucide-react";
import { mockResources } from "../../mockData";
import { useGetResourceTypeList, useGetResourceSubtypeList, useGetUtilityServices, useChoices, useMeterManufacturers, useMeterModels, useWarehouses } from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useConfigMapJson } from "@shared/api/hooks";
import { useAddResource, useSearchSubmeters, useMeterDetail, useGenerateMeterNumber, useEditResource, useGetMasterMeters } from "../../hook";
import { convertToSnakeCase } from "@shared/utils/caseConverter";
import { queryClient } from "@shared/api/queries/queryClient";

export default function AddToWarehouse() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  const { refetch: fetchGeneratedId, isFetching: isGenerating } = useGenerateMeterNumber(remoteUtilityId || "", false);
  const { mutate: addResourceMutate } = useAddResource();
  const { mutate: editResourceMutate } = useEditResource();
  const [watchResourceType, setWatchResourceType] = React.useState<string>("");
  const [watchSubType, setWatchSubType] = React.useState<string>("");

  const { data: resourceTypeResponse } = useGetResourceTypeList(remoteUtilityId);
  const { data: subtypesResponse } = useGetResourceSubtypeList(
    remoteUtilityId,
    watchResourceType ? [watchResourceType] : undefined
  );
  const subtypesList = subtypesResponse?.result || [];

  const resourceTypeOptions = React.useMemo(() => {
    const results = resourceTypeResponse?.result || resourceTypeResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [resourceTypeResponse]);

  const { data: utilityServicesData } = useGetUtilityServices(remoteUtilityId);

  const utilityServiceOptions = React.useMemo(() => {
    return Array.isArray(utilityServicesData)
      ? utilityServicesData
        .filter((item: any) => item.isActive)
        .map((item: any) => ({
          label: item.name,
          value: item.name,
        }))
      : [];
  }, [utilityServicesData]);

  const [watchCategory, setWatchCategory] = React.useState<string>("");

  const { data: categoryConfig } = useConfigMapJson(
    "category_json",
    remoteUtilityId,
    "category"
  );

  const { data: subCategoryConfig } = useConfigMapJson(
    "category_json",
    remoteUtilityId,
    "sub_category",
    watchCategory || undefined
  );

  const categoryOptions = React.useMemo(() => {
    return Array.isArray(categoryConfig)
      ? categoryConfig.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [categoryConfig]);

  const subCategoryOptions = React.useMemo(() => {
    return Array.isArray(subCategoryConfig)
      ? subCategoryConfig.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [subCategoryConfig]);

  const { data: classificationChoices } = useChoices({
    choice: "MeterOwnership",
    remoteUtilityId,
  });

  const classificationOptions = React.useMemo(() => {
    return Array.isArray(classificationChoices)
      ? classificationChoices.map((item: any) => ({
        label: item.value,
        value: item.key,
      }))
      : [];
  }, [classificationChoices]);

  const { data: masterMeters } = useGetMasterMeters(remoteUtilityId);

  const masterResourceOptions = React.useMemo(() => {
    return Array.isArray(masterMeters)
      ? masterMeters.map((item: any) => ({
        label: `${item.meterNumber} (${item.deviceNo})`,
        value: item.id,
      }))
      : [];
  }, [masterMeters]);

  const { data: manufacturerResponse } = useMeterManufacturers({
    remote_utility_id: remoteUtilityId || "",
    is_pagination_required: false,
  });

  const manufacturerOptions = React.useMemo(() => {
    const results = manufacturerResponse?.results || manufacturerResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.name,
        value: item.id,
      }))
      : [];
  }, [manufacturerResponse]);

  const [watchManufacturer, setWatchManufacturer] = React.useState<string>("");

  const { data: modelsResponse } = useMeterModels(
    remoteUtilityId || "",
    watchManufacturer || undefined
  );

  const modelOptions = React.useMemo(() => {
    const results = modelsResponse?.results || modelsResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.meterModel,
        value: item.id,
      }))
      : [];
  }, [modelsResponse]);

  const { data: warehousesResponse } = useWarehouses(remoteUtilityId);

  const warehouseOptions = React.useMemo(() => {
    const results = warehousesResponse?.result || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.name,
        value: item.id,
      }))
      : [];
  }, [warehousesResponse]);

  const { data: subMeters } = useSearchSubmeters(remoteUtilityId);

  const subMeterOptions = React.useMemo(() => {
    const results = subMeters?.results || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: `${item.meterNumber} (${item.deviceNo})`,
        value: item.id,
      }))
      : [];
  }, [subMeters]);

  const isEditMode = !!id;
  const { data: apiResource } = useMeterDetail(id || "", remoteUtilityId);
  const existingResource = isEditMode ? apiResource : null;

  const initialValues = {
    warehouse: existingResource?.warehouse || "",
    resourceId: existingResource?.id || "",
    utilityService: existingResource?.utilityService || "",
    type: existingResource?.type || "",
    subType: existingResource?.subType || "",
    classification: existingResource?.classification || "",
    serialNo: existingResource?.serialNo || "",
    category: existingResource?.category || "",
    subCategory: existingResource?.subCategory || "",
    manufacturer: existingResource?.manufacturer || "",
    model: existingResource?.model || "",
    parent_meter: existingResource?.parentMeter || "",
  };

  // Base fields that are always present
  const baseFormFields: FormField[] = React.useMemo(() => [
    // --- Section 1: Resource Information ---
    {
      name: "warehouse",
      type: "select",
      label: "Warehouse",
      placeholder: "Select warehouse",
      required: true,
      helperText: "Select the warehouse where this resource is stored.",
      className: "col-span-5",
      group: "Resource Information",
      groupHelperText: "Select the resource type and sub type to configure this resource.",
      groupBorderNeeded: false,
      options: warehouseOptions,
    },
    {
      name: "resourceId",
      type: "text",
      label: "Resource ID",
      placeholder: "e.g. RES-0001",
      required: true,
      helperText: "Unique identifier — format: RES-XXXX",
      className: "col-span-4",
      group: "Resource Information",
      groupBorderNeeded: false,
    },
    {
      name: "generateButton",
      type: "button",
      label: "Generate ID",
      buttonLabel: isGenerating ? "Generating..." : "Generate",
      disabled: isGenerating || !remoteUtilityId,
      className: "col-span-1 mt-2",
      helperText: "Click to generate resource id.",
      group: "Resource Information",
      groupBorderNeeded: false,
      onClick: async () => {
        try {
          const res = await fetchGeneratedId();
          if (res.data && res.data.meterNumber) {
            form.setValue("resourceId", res.data.meterNumber, { shouldValidate: true });
            toast({ title: "Generated", description: `Resource ID generated: ${res.data.meterNumber}` });
          }
        } catch (error) {
          toast({ title: "Error", description: "Failed to generate Resource ID", variant: "destructive" });
        }
      },
    },
    {
      name: "serialNo",
      type: "text",
      label: "Serial Number",
      placeholder: "e.g. SN-HTX-00101",
      helperText: "Unique serial number printed on the physical device.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
    },
    {
      name: "utilityService",
      type: "select",
      label: "Utility Service",
      placeholder: "Select utility service",
      required: true,
      helperText: "Select the utility service type.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: utilityServiceOptions,
    },
    {
      name: "type",
      type: "select",
      label: "Resource Type",
      placeholder: "Select resource type",
      required: true,
      helperText: "Select the category of resource being added.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: resourceTypeOptions,
    },
    {
      name: "subType",
      type: "select",
      label: "Resource Sub Type",
      placeholder: "select a type first",
      required: true,
      helperText: "Sub type determines which fields are collected.",
      disabled: (formValues) => !formValues.type,
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: subtypesList.map((item: any) => ({
        label: item.name,
        value: item.code,
      })),
    },
    {
      name: "classification",
      type: "select",
      label: "Resource Classification",
      placeholder: "Select classification",
      required: true,
      helperText: "Define the role of this resource within the hierarchy.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: classificationOptions,
    },
    {
      name: "parent_meter",
      type: "select",
      label: "Master Resource",
      placeholder: "Select master resource",
      required: true,
      helperText: "Select the master resource this sub resource belongs to.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: masterResourceOptions,
      showWhen: (formValues) => formValues.classification === 2 || formValues.classification === "2",
    },
    {
      name: "category",
      type: "select",
      label: "Category",
      placeholder: "Select category",
      helperText: "Broad category this resource belongs to.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: categoryOptions,
    },
    {
      name: "subCategory",
      type: "select",
      label: "Sub-Category",
      placeholder: "select a category first",
      helperText: "Specific sub-category within the selected category.",
      disabled: (formValues) => !formValues.category,
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: subCategoryOptions,
    },
    {
      name: "manufacturer",
      type: "select",
      label: "Manufacturer",
      placeholder: "Select manufacturer",
      helperText: "Company that manufactured this resource.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: manufacturerOptions,
    },
    {
      name: "model",
      type: "select",
      label: "Model",
      placeholder: "select a manufacturer first",
      helperText: "Specific model of the resource.",
      disabled: (formValues) => !formValues.manufacturer,
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: modelOptions,
    },
    {
      name: "linkedSubMeters",
      type: "multi-select",
      label: "Link Sub Resources",
      placeholder: "Search sub resources...",
      helperText: "Search and select existing sub resources to attach to this master.",
      className: "col-span-5",

      group: "Resource Information",
      groupBorderNeeded: false,
      options: subMeterOptions,
      showWhen: (formValues) => formValues.classification === 0 || formValues.classification === "0",
    },
  ], [resourceTypeOptions, subtypesList, utilityServiceOptions, categoryOptions, subCategoryOptions, classificationOptions, manufacturerOptions, modelOptions, warehouseOptions, subMeterOptions, masterResourceOptions]);

  // Dynamically resolve custom fields based on selected subType config
  const dynamicFields: FormField[] = React.useMemo(() => {
    if (!watchSubType) return [];

    // Search for subtype matching current value
    const selected = subtypesList.find((s: any) => s.code === watchSubType);

    const target = selected;

    if (!target) return [];

    const apiCustomFields = target.customFields || target.custom_fields || [];

    // If customFields array exists
    if (Array.isArray(apiCustomFields) && apiCustomFields.length > 0) {
      return apiCustomFields.map((cf: any) => {
        const labelStr = cf.label || cf.fieldLabel || cf.field_label || "";
        const rawType = (cf.type || "text").toLowerCase();
        let fieldType = rawType;
        if (rawType === "radio" || rawType === "yes/no") {
          fieldType = "radio";
        } else if (rawType === "switch") {
          fieldType = "switch";
        } else if (rawType === "dropdown" || rawType === "select") {
          fieldType = "select";
        }

        const fieldName = cf.name || cf.fieldName || cf.field_name || cf.key || `custom${labelStr.charAt(0).toUpperCase() + labelStr.slice(1).replace(/\s+/g, "")}`;

        return {
          name: fieldName,
          type: fieldType,
          label: labelStr,
          placeholder: cf.placeholder || `Enter ${labelStr.toLowerCase()}`,
          required: cf.required !== undefined
            ? cf.required
            : (cf.isRequired !== undefined
              ? cf.isRequired
              : (cf.is_required !== undefined ? cf.is_required : false)),
          className: "col-span-5",
          group: "Specifications",
          groupHelperText: "Configure specification details for the selected sub type.",
          groupBorderNeeded: false,
          options: Array.isArray(cf.options)
            ? cf.options.map((o: any) => typeof o === "string" ? { label: o, value: o } : o)
            : (fieldType === "radio" ? [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] : []),
        };
      });
    }

    // Comma-separated fields fallback
    if (target.fields && target.fields !== "None") {
      return target.fields.split(",").map((fieldStr: string) => {
        const label = fieldStr.trim();
        const lowerLabel = label.toLowerCase();
        let fieldType = "text";
        if (lowerLabel.includes("count") || lowerLabel.includes("read") || lowerLabel.includes("rate")) {
          fieldType = "number";
        }
        const fieldName = `custom${label.charAt(0).toUpperCase() + label.slice(1).replace(/\s+/g, "")}`;
        return {
          name: fieldName,
          type: fieldType,
          label: label,
          placeholder: `Enter ${lowerLabel}`,
          required: false,
          className: "col-span-5",
          group: "Specifications",
          groupHelperText: "Configure specification details for the selected sub type.",
          groupBorderNeeded: false,
        };
      });
    }

    return [];
  }, [watchSubType, subtypesList]);

  const formFields = React.useMemo(() => {
    return [...baseFormFields, ...dynamicFields];
  }, [baseFormFields, dynamicFields]);

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: initialValues,
  });

  const watchedResourceId = form.watch("resourceId");

  const currentResourceType = form.watch("type");
  useEffect(() => {
    setWatchResourceType(currentResourceType || "");
  }, [currentResourceType]);

  const currentCategory = form.watch("category");
  useEffect(() => {
    setWatchCategory(currentCategory || "");
  }, [currentCategory]);

  const currentManufacturer = form.watch("manufacturer");
  useEffect(() => {
    setWatchManufacturer(currentManufacturer || "");
  }, [currentManufacturer]);

  const currentSubType = form.watch("subType");
  useEffect(() => {
    setWatchSubType(currentSubType || "");
  }, [currentSubType]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (existingResource) {
      console.log("EXISTING_RESOURCE_JSON:", JSON.stringify(existingResource));

      const formValues: Record<string, any> = {
        warehouse: (typeof existingResource.warehouse === "object" ? existingResource.warehouse?.id : existingResource.warehouse) || "",
        resourceId: existingResource.meterNumber || "",
        utilityService: existingResource.productCode || "",
        type: existingResource.resourceTypeCode || "",
        subType: existingResource.resourceSubTypeCode || "",
        classification: existingResource.ownership || "",
        serialNo: existingResource.deviceNo || "",
        category: existingResource.category || "",
        subCategory: existingResource.subCategory || "",
        manufacturer: (typeof existingResource.meterMake === "object" ? existingResource.meterMake?.id : existingResource.meterMake) || "",
        model: existingResource.meterModel?.id || existingResource.meterModel || "",
        parent_meter: (typeof existingResource.parentMeter === "object" ? existingResource.parentMeter?.id : existingResource.parentMeter) || "",
        linkedSubMeters: existingResource?.linkedSubMeters?.map((submeter: any) => submeter.id) || [],
      };

      const customFields =
        existingResource?.additionalData?.resourceCustomFields

      if (customFields && typeof customFields === "object") {
        Object.assign(formValues, customFields);
      }

      form.reset(formValues);
    }
  }, [existingResource]);

  const handleSubmit = (data: any) => {

    const dynamicPayload: any = {};
    dynamicFields.forEach((field) => {
      if (data[field.name] !== undefined) {
        dynamicPayload[field.name] = data[field.name];
      }
    });

    const payload: any = {
      meter_number: data.resourceId,
      device_no: data.serialNo,
      product_code: data.utilityService,
      category: data.category,
      sub_category: data.subCategory,
      meter_make: data.manufacturer,
      meter_model: data.model,
      remote_utility_id: remoteUtilityId,
      physical_status: 2,
      ownership: data.classification,
      resource_type_code: data.type,
      resource_sub_type_code: data.subType,
      warehouse: data.warehouse,
      additional_data: { resource_custom_fields: dynamicPayload },
    };

    if (data.classification === 0 || data.classification === "0") {
      payload.linked_sub_meters = data.linkedSubMeters || [];
    }

    if (data.classification === 2 || data.classification === "2") {
      payload.parent_meter = data.parent_meter ? Number(data.parent_meter) : undefined;
    }

    if (isEditMode) {
      editResourceMutate(
        { resourceId: id, payload },
        {
          onSuccess: () => {
            queryClient.refetchQueries({
              queryKey: ["mx", "resources-list"],
            });
            queryClient.refetchQueries({
              queryKey: ["mx", "meter-summary"],
            });
          },
        }
      );
    } else {
      addResourceMutate(payload, {
        onSuccess: () => {
          queryClient.refetchQueries({
            queryKey: ["mx", "resources-list"],
          });
          queryClient.refetchQueries({
            queryKey: ["mx", "meter-summary"],
          });
          navigate("/mx/manage/resources");
        }
      });
    }
  };

  return (
    <div className="p-3">
      <PageHeader
        title={isEditMode ? "Edit details" : "Add to inventory"}
        subtitle={isEditMode ? "Update details of the warehouse resource." : "Fill in the details to add a new resource to inventory."}
        icon={ArrowLeft}
        onIconClick={() => navigate("/mx/manage/resources")}
      >
        <span className="text-xs font-bold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full border border-gray-200">
          {watchedResourceId ? `${watchedResourceId}` : "New Resource"}
        </span>
      </PageHeader>

      <div className="bg-white rounded-xl border border-gray-150 p-6 shadow-sm mt-2">
        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onReset={() => navigate("/mx/manage/resources")}
          gridColumns={10}
          responsiveColumns={{ sm: 1, md: 10 }}
          config={{
            showResetButton: true,
            showSubmitButton: true,
            submitButtonText: isEditMode ? "Save Changes" : "Add to inventory",
            resetButtonText: "Cancel",
            classes: FIELD_CLASSES,
          }}
        />
      </div>
    </div>
  );
}
